<template>
  <div class="patient-incubator-detail">
    <!-- Header -->
    <div class="page-header">
      <b-button variant="link" class="back-btn" @click="$router.back()">
        <feather-icon icon="ChevronLeftIcon" size="20" />
        <span>{{ $t("common.back") }}</span>
      </b-button>
      <div class="header-title">
        <h1>{{ $t("incubator.patient-incubator-details") }}</h1>
        <div class="header-meta">
          <b-badge
            :variant="model && model.is_active ? 'success' : 'secondary'"
            pill
            class="status-badge"
          >
            {{
              model && model.is_active
                ? $t("common.active")
                : $t("common.inactive")
            }}
          </b-badge>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div v-if="!loading && !error && model" class="action-buttons-section">
      <div class="action-buttons">
        <b-button
          v-if="
            $store.state.app.permissions.includes('get-patientCardTest') &&
            model.is_active
          "
          variant="primary"
          size="sm"
          @click="$bvModal.show('modal-add-lab-test')"
        >
          <feather-icon icon="TestTubeIcon" size="16" />
          {{ $t("reception.add-lab-test") }}
        </b-button>
        <b-button
          v-if="model.is_active"
          variant="primary"
          size="sm"
          @click="$bvModal.show('modal-add-drug')"
        >
          <feather-icon icon="DropletIcon" size="16" />
          {{ $t("reception.add-drug") }}
        </b-button>
        <b-button
          v-if="
            $store.state.app.permissions.includes('get-patientCardRadiology') &&
            model.is_active
          "
          variant="primary"
          size="sm"
          @click="$bvModal.show('modal-add-radiology')"
        >
          <feather-icon icon="ImageIcon" size="16" />
          {{ $t("reception.add-radiology") }}
        </b-button>
        <b-dropdown
          v-if="model.is_active"
          variant="outline-secondary"
          size="sm"
          :text="$t('incubator.print-documents')"
          menu-class="print-documents-dropdown"
          class="print-documents-dropdown-btn"
        >
          <b-dropdown-item
            link-class="d-flex align-items-center"
            @click="printBirthCertificateCommitment"
          >
            <feather-icon icon="FileTextIcon" size="16" class="mr-50" />
            <span>{{
              $t("incubator.print-birth-certificate-commitment")
            }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            link-class="d-flex align-items-center"
            @click="printNICUDischargeAgainstAdvice"
          >
            <feather-icon icon="LogOutIcon" size="16" class="mr-50" />
            <span>{{ $t("incubator.print-discharge-against-advice") }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Loading / Error -->
    <div v-if="loading" class="loading-state">
      <b-spinner variant="primary" label="Loading..." />
      <p>{{ $t("common.loading") }}...</p>
    </div>
    <b-alert v-else-if="error" variant="danger" show class="error-alert">
      <feather-icon icon="AlertCircleIcon" size="20" />
      <span>{{ error }}</span>
    </b-alert>

    <!-- Content -->
    <div v-else>
      <!-- Main Card -->
      <div class="main-card">
        <div class="card-section patient-section">
          <div class="section-icon patient-icon">
            <feather-icon icon="UserIcon" size="24" />
          </div>
          <div class="section-content">
            <h2 class="patient-name">
              {{ formatPatientName(model) }}
            </h2>
            <div class="patient-meta">
              <div v-if="patientCardNo" class="meta-item">
                <feather-icon icon="CreditCardIcon" size="16" />
                <span>{{ $t("reception.card-no") }} #{{ patientCardNo }}</span>
              </div>
              <div v-if="patientNo" class="meta-item">
                <feather-icon icon="HashIcon" size="16" />
                <span>{{ $t("reception.patient-no") }} {{ patientNo }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="divider" />

        <div class="card-section incubator-section">
          <div class="section-icon incubator-icon">
            <feather-icon icon="CpuIcon" size="24" />
          </div>
          <div class="section-content">
            <h3 class="incubator-name">
              {{ incubatorName }}
            </h3>
            <p v-if="incubatorBrand" class="incubator-brand">
              {{ $t("incubator.brand") }}: {{ incubatorBrand }}
            </p>
            <p v-if="incubatorType" class="incubator-type">
              {{ $t("incubator.type") }}: {{ incubatorType }}
            </p>
          </div>
        </div>

        <div class="divider" />

        <!-- Time Status -->
        <div
          class="time-status"
          :class="{ expired: isPaymentExpired(model), active: model.is_active }"
        >
          <div class="time-icon-wrapper">
            <feather-icon
              icon="ClockIcon"
              size="32"
              :class="model.is_active ? getPaymentTimerClass(model) : ''"
            />
          </div>
          <div class="time-content">
            <p class="time-label">
              {{
                model.is_active
                  ? $t("incubator.time-remaining")
                  : $t("common.status")
              }}
            </p>
            <h3 class="time-value">
              <template v-if="model.is_active">
                {{ getTimeRemaining(model) }}
              </template>
              <template v-else>
                {{ $t("incubator.inactive_checkout_done") }}
              </template>
            </h3>
          </div>
        </div>
      </div>

      <!-- Info Grid -->
      <div class="info-grid">
        <!-- Timing Card -->
        <div class="info-card">
          <div class="info-card-header">
            <feather-icon icon="CalendarIcon" size="20" />
            <h4>{{ $t("incubator.timing") }}</h4>
          </div>
          <div class="info-list">
            <div class="info-item">
              <span class="info-label">{{ $t("incubator.start-time") }}</span>
              <span class="info-value">{{
                formatDateTime(model.start_time)
              }}</span>
            </div>
            <div class="info-item highlight">
              <span class="info-label">{{ $t("incubator.paid-until") }}</span>
              <span class="info-value">{{
                formatDateTime(model.paid_until)
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t("incubator.end-time") }}</span>
              <span class="info-value">{{
                model.end_time ? formatDateTime(model.end_time) : "—"
              }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t("incubator.duration") }}</span>
              <span class="info-value">{{
                calculateDuration(model.start_time, model.end_time)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Patient Details Card -->
        <div class="info-card">
          <div class="info-card-header">
            <feather-icon icon="UserIcon" size="20" />
            <h4>{{ $t("clinic.patient-details") }}</h4>
          </div>
          <div class="info-list">
            <div class="info-item">
              <span class="info-label">{{ $t("reception.patient-name") }}</span>
              <span class="info-value">{{ patientName }}</span>
            </div>
            <div v-if="motherName" class="info-item">
              <span class="info-label">{{ $t("clinic.mother-name") }}</span>
              <span class="info-value">{{ motherName }}</span>
            </div>

            <div v-if="gender" class="info-item">
              <span class="info-label">{{ $t("reception.gender") }}</span>
              <span class="info-value">{{
                $t(`common.${gender.toLowerCase()}`)
              }}</span>
            </div>
            <div v-if="phone" class="info-item">
              <span class="info-label">{{ $t("common.phone") }}</span>
              <div class="info-value">
                <span class="ltr">{{ phone }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Medical Information Card -->
        <div class="info-card full-width">
          <div class="info-card-header">
            <feather-icon icon="FileTextIcon" size="20" />
            <h4>{{ $t("common.medical-information") }}</h4>
          </div>
          <div class="info-list">
            <div v-if="model.description" class="info-item vertical">
              <span class="info-label">{{ $t("common.description") }}</span>
              <p class="info-value medical-text">
                {{ model.description }}
              </p>
            </div>
            <div v-if="model.result" class="info-item vertical">
              <span class="info-label">{{ $t("incubator.result") }}</span>
              <p class="info-value medical-text">
                {{ model.result }}
              </p>
            </div>
            <div v-if="!model.description && !model.result" class="no-data">
              <feather-icon icon="InfoIcon" size="16" />
              <span>{{ $t("common.no-medical-info") }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Admission Notes Section -->
      <div class="admission-notes-section">
        <div class="section-header">
          <div class="section-title">
            <feather-icon icon="ClipboardIcon" size="24" />
            <h3>{{ $t("incubator.admission-notes") }}</h3>
            <span class="count-badge">{{ admissionNotes.length }}</span>
          </div>
          <b-button
            variant="primary"
            class="add-note-btn"
            @click="openAdmissionNoteModal(null)"
          >
            <feather-icon icon="PlusIcon" size="18" />
            <span>{{ $t("incubator.add-admission-note") }}</span>
          </b-button>
        </div>

        <div v-if="admissionNotes && admissionNotes.length" class="notes-list">
          <div v-for="note in admissionNotes" :key="note.id" class="note-card">
            <div class="note-header">
              <div class="note-datetime">
                <feather-icon icon="CalendarIcon" size="16" />
                <span>{{ formatDateTime(note.datetime) }}</span>
              </div>
              <div class="note-actions">
                <b-button
                  size="sm"
                  variant="outline-success"
                  class="mr-1"
                  @click="printAdmissionNote(note)"
                >
                  <feather-icon icon="PrinterIcon" size="14" />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-info"
                  class="mr-1"
                  @click="openAdmissionNoteModal(note)"
                >
                  <feather-icon icon="EditIcon" size="14" />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-danger"
                  @click="deleteAdmissionNote(note.id)"
                >
                  <feather-icon icon="TrashIcon" size="14" />
                </b-button>
              </div>
            </div>
            <div class="note-content">
              <div class="note-fields-grid">
                <div
                  v-if="getNoteValue(note, 'patient_name')"
                  class="note-field full-width"
                >
                  <span class="field-label"
                    >{{ $t("reception.patient-name") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "patient_name")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'tob')" class="note-field">
                  <span class="field-label">{{ $t("incubator.tob") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "tob")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'toa')" class="note-field">
                  <span class="field-label">{{ $t("incubator.toa") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "toa")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'dob')" class="note-field">
                  <span class="field-label">{{ $t("incubator.dob") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "dob")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'doa')" class="note-field">
                  <span class="field-label">{{ $t("incubator.doa") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "doa")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'wt')" class="note-field">
                  <span class="field-label">{{ $t("incubator.wt") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "wt")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'referro_from')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.referro-from") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "referro_from")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'pediatrician_s')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.pediatrician-s") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "pediatrician_s")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'pediatrician_p')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.pediatrician-p") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "pediatrician_p")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'cc')"
                  class="note-field full-width"
                >
                  <span class="field-label">{{ $t("incubator.cc") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "cc")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'ght')" class="note-field">
                  <span class="field-label">{{ $t("incubator.ght") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "ght")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'gdm')" class="note-field">
                  <span class="field-label">{{ $t("incubator.gdm") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "gdm")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'ga')" class="note-field">
                  <span class="field-label">{{ $t("incubator.ga") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "ga")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'g')" class="note-field">
                  <span class="field-label">{{ $t("incubator.g") }}:</span>
                  <span class="field-value">{{ getNoteValue(note, "g") }}</span>
                </div>
                <div v-if="getNoteValue(note, 'p')" class="note-field">
                  <span class="field-label">{{ $t("incubator.p") }}:</span>
                  <span class="field-value">{{ getNoteValue(note, "p") }}</span>
                </div>
                <div v-if="getNoteValue(note, 'a')" class="note-field">
                  <span class="field-label">{{ $t("incubator.a") }}:</span>
                  <span class="field-value">{{ getNoteValue(note, "a") }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'brief_note')"
                  class="note-field full-width"
                >
                  <span class="field-label"
                    >{{ $t("incubator.brief-note") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "brief_note")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'aph')" class="note-field">
                  <span class="field-label">{{ $t("incubator.aph") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "aph")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'll')" class="note-field">
                  <span class="field-label">{{ $t("incubator.ll") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "ll")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'poly_oligo')" class="note-field">
                  <span class="field-label"
                    >{{ $t("incubator.poly-oligo") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "poly_oligo")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'admitted_on_cpap')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.admitted-on-cpap") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "admitted_on_cpap")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'fever')" class="note-field">
                  <span class="field-label">{{ $t("incubator.fever") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "fever")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'apgar')" class="note-field">
                  <span class="field-label">{{ $t("incubator.apgar") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "apgar")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'uti')" class="note-field">
                  <span class="field-label">{{ $t("incubator.uti") }}:</span>
                  <span class="field-value">{{
                    getNoteValue(note, "uti")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'previous_admission_to_nicu')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.previous-admission-to-nicu") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "previous_admission_to_nicu")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'mother_blood_g')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.mother-blood-g") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "mother_blood_g")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'consanguinty')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.consanguinty") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "consanguinty")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'baby_blood_g')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.baby-blood-g") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "baby_blood_g")
                  }}</span>
                </div>
                <div v-if="getNoteValue(note, 'steroid')" class="note-field">
                  <span class="field-label"
                    >{{ $t("incubator.steroid") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "steroid")
                  }}</span>
                </div>
                <div
                  v-if="getNoteValue(note, 'thyrohd_disease')"
                  class="note-field"
                >
                  <span class="field-label"
                    >{{ $t("incubator.thyrohd-disease") }}:</span
                  >
                  <span class="field-value">{{
                    getNoteValue(note, "thyrohd_disease")
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-notes">
          <feather-icon icon="ClipboardIcon" size="48" />
          <p>{{ $t("incubator.no-admission-notes") }}</p>
        </div>
      </div>

      <!-- Patient Radiology Section -->
      <div class="patient-radiology-section">
        <div class="section-header">
          <div class="section-title">
            <feather-icon icon="CameraIcon" size="24" />
            <h3>{{ $t("radiology.patient-radiology") }}</h3>
            <span v-if="radiologyPatientData.length > 0" class="count-badge">
              {{ radiologyPatientData.length }}
            </span>
          </div>
        </div>

        <div v-if="radiologyPatientData.length > 0" class="radiology-grid">
          <div
            v-for="radiology in radiologyPatientData"
            :key="radiology.id"
            class="radiology-card"
          >
            <div class="radiology-card-header">
              <div class="radiology-info">
                <h5 class="radiology-name">
                  {{ getRadiologyName(radiology.radiology_id) }}
                </h5>
                <span class="attachment-count">
                  {{ radiology.attachments ? radiology.attachments.length : 0 }}
                  {{ $t("common.attachments") }}
                </span>
              </div>
            </div>

            <div
              v-if="radiology.attachments && radiology.attachments.length"
              class="attachments-grid"
            >
              <div
                v-for="attachment in radiology.attachments"
                :key="attachment.id"
                class="attachment-card"
              >
                <div class="attachment-preview">
                  <div
                    v-if="!attachment.attachment_url.includes('pdf')"
                    class="attachment-image-wrapper"
                  >
                    <img
                      :src="attachment.attachment_url"
                      alt="attachment"
                      class="attachment-image"
                    />
                    <div class="attachment-hover-overlay">
                      <feather-icon icon="EyeIcon" size="24" />
                    </div>
                  </div>
                  <div v-else class="attachment-pdf-wrapper">
                    <feather-icon icon="FileTextIcon" size="32" class="pdf-icon" />
                    <span class="pdf-label">{{ $t("common.pdf-file") }}</span>
                  </div>
                </div>
                <div class="attachment-action">
                  <b-button
                    variant="primary"
                    size="sm"
                    block
                    @click="openAttachment(attachment.attachment_url)"
                  >
                    <feather-icon icon="EyeIcon" size="14" class="mr-50" />
                    {{ $t("common.view") }}
                  </b-button>
                </div>
              </div>
            </div>

            <div v-else class="no-attachments-state">
              <feather-icon icon="ImageIcon" size="32" class="no-attachments-icon" />
              <p class="no-attachments-text">{{ $t("common.no-attachments") }}</p>
            </div>
          </div>
        </div>

        <div v-else class="empty-radiology-state">
          <feather-icon icon="CameraIcon" size="48" />
          <h4>{{ $t("radiology.no-radiology") }}</h4>
          <p>{{ $t("radiology.no-radiology-description") }}</p>
        </div>
      </div>

      <!-- Invoices Section -->
      <div class="invoices-section">
        <div class="section-header">
          <div class="section-title">
            <feather-icon icon="FileIcon" size="24" />
            <h3>{{ $t("incubator.invoices") }}</h3>
            <span class="count-badge">{{ invoices.length }}</span>
          </div>
          <!-- <b-button
            v-if="canUpdate && model.is_active"
            variant="success"
            class="add-invoice-btn"
            @click="openCreateInvoiceModal()"
          >
            <feather-icon icon="PlusIcon" size="18" />
            <span>{{ $t("incubator.create-invoice") }}</span>
          </b-button> -->
        </div>

        <div v-if="invoices && invoices.length" class="invoices-grid">
          <InvoiceCard
            v-for="invoice in invoices"
            :key="invoice.id"
            :invoice="invoice"
            @onChangeStatus="fetchInvoices"
          />
        </div>
        <div v-else class="no-invoices">
          <feather-icon icon="FileIcon" size="48" />
          <p>{{ $t("common.no-invoices-yet") }}</p>
        </div>
      </div>

      <!-- Action Button -->
      <div v-if="model.is_active" class="action-section">
        <b-button
          variant="warning"
          size="lg"
          class="checkout-btn"
          @click="openCheckoutConfirm()"
        >
          <feather-icon icon="LogOutIcon" size="20" />
          <span>{{ $t("incubator.checkout") }}</span>
        </b-button>
      </div>
    </div>

    <!-- Admission Note Modal -->
    <b-modal
      id="modal-admission-note"
      :title="
        editingNote
          ? $t('incubator.edit-admission-note')
          : $t('incubator.add-admission-note')
      "
      size="lg"
      hide-footer
      centered
      modal-class="modern-modal"
      :no-close-on-backdrop="true"
    >
      <div class="modal-content-wrapper">
        <b-form @submit.prevent="saveAdmissionNote">
          <b-row>
            <b-col md="6" cols="12">
              <b-form-group
                :label="$t('common.datetime')"
                label-for="note_datetime"
                class="form-group-modern required"
              >
                <b-form-input
                  id="note_datetime"
                  v-model="noteForm.datetime"
                  type="datetime-local"
                  size="lg"
                />
                <p v-if="noteErrors.datetime" class="error-text">
                  {{ noteErrors.datetime }}
                </p>
              </b-form-group>
            </b-col>
            <b-col md="6" cols="12">
              <b-form-group
                :label="$t('reception.patient-name')"
                label-for="patient_name"
                class="form-group-modern"
              >
                <b-form-input
                  id="patient_name"
                  v-model="noteForm.notes.patient_name"
                  :placeholder="$t('reception.patient-name')"
                  size="lg"
                />
                <small class="text-muted">{{
                  $t("common.auto-filled-editable")
                }}</small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.tob')" label-for="tob">
                <b-form-input
                  id="tob"
                  v-model="noteForm.notes.tob"
                  type="time"
                  :placeholder="$t('incubator.tob')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.toa')" label-for="toa">
                <b-form-input
                  id="toa"
                  v-model="noteForm.notes.toa"
                  type="time"
                  :placeholder="$t('incubator.toa')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.dob')" label-for="dob">
                <b-form-input
                  id="dob"
                  v-model="noteForm.notes.dob"
                  type="date"
                  :placeholder="$t('incubator.dob')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.doa')" label-for="doa">
                <b-form-input
                  id="doa"
                  v-model="noteForm.notes.doa"
                  type="date"
                  :placeholder="$t('incubator.doa')"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.age')" label-for="age">
                <b-form-input
                  id="age"
                  v-model="noteForm.notes.age"
                  :placeholder="$t('incubator.age')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.wt')" label-for="wt">
                <b-form-input
                  id="wt"
                  v-model="noteForm.notes.wt"
                  :placeholder="$t('incubator.wt')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group
                :label="$t('incubator.referro-from')"
                label-for="referro_from"
              >
                <b-form-input
                  id="referro_from"
                  v-model="noteForm.notes.referro_from"
                  :placeholder="$t('incubator.referro-from')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group
                :label="$t('incubator.pediatrician-s')"
                label-for="pediatrician_s"
              >
                <b-form-input
                  id="pediatrician_s"
                  v-model="noteForm.notes.pediatrician_s"
                  :placeholder="$t('incubator.pediatrician-s')"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="4" cols="12">
              <b-form-group
                :label="$t('incubator.pediatrician-p')"
                label-for="pediatrician_p"
              >
                <b-form-input
                  id="pediatrician_p"
                  v-model="noteForm.notes.pediatrician_p"
                  :placeholder="$t('incubator.pediatrician-p')"
                />
              </b-form-group>
            </b-col>
            <b-col md="8" cols="12">
              <b-form-group :label="$t('incubator.cc')" label-for="cc">
                <b-form-textarea
                  id="cc"
                  v-model="noteForm.notes.cc"
                  :placeholder="$t('incubator.cc')"
                  rows="2"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.ght')" label-for="ght">
                <b-form-input
                  id="ght"
                  v-model="noteForm.notes.ght"
                  :placeholder="$t('incubator.ght')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.gdm')" label-for="gdm">
                <b-form-input
                  id="gdm"
                  v-model="noteForm.notes.gdm"
                  :placeholder="$t('incubator.gdm')"
                />
              </b-form-group>
            </b-col>
            <b-col md="2" cols="4">
              <b-form-group :label="$t('incubator.ga')" label-for="ga">
                <b-form-input
                  id="ga"
                  v-model="noteForm.notes.ga"
                  :placeholder="$t('incubator.ga')"
                />
              </b-form-group>
            </b-col>
            <b-col md="1" cols="4">
              <b-form-group :label="$t('incubator.g')" label-for="g">
                <b-form-input
                  id="g"
                  v-model="noteForm.notes.g"
                  :placeholder="$t('incubator.g')"
                />
              </b-form-group>
            </b-col>
            <b-col md="1" cols="4">
              <b-form-group :label="$t('incubator.p')" label-for="p">
                <b-form-input
                  id="p"
                  v-model="noteForm.notes.p"
                  :placeholder="$t('incubator.p')"
                />
              </b-form-group>
            </b-col>
            <b-col md="2" cols="12">
              <b-form-group :label="$t('incubator.a')" label-for="a">
                <b-form-input
                  id="a"
                  v-model="noteForm.notes.a"
                  :placeholder="$t('incubator.a')"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-form-group
                :label="$t('incubator.brief-note')"
                label-for="brief_note"
              >
                <b-form-textarea
                  id="brief_note"
                  v-model="noteForm.notes.brief_note"
                  :placeholder="$t('incubator.brief-note')"
                  rows="3"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.aph')" label-for="aph">
                <b-form-input
                  id="aph"
                  v-model="noteForm.notes.aph"
                  :placeholder="$t('incubator.aph')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.ll')" label-for="ll">
                <b-form-input
                  id="ll"
                  v-model="noteForm.notes.ll"
                  :placeholder="$t('incubator.ll')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group
                :label="$t('incubator.poly-oligo')"
                label-for="poly_oligo"
              >
                <b-form-input
                  id="poly_oligo"
                  v-model="noteForm.notes.poly_oligo"
                  :placeholder="$t('incubator.poly-oligo')"
                />
              </b-form-group>
            </b-col>
            <b-col md="6" cols="12">
              <b-form-group
                :label="$t('incubator.admitted-on-cpap')"
                label-for="admitted_on_cpap"
              >
                <b-form-input
                  id="admitted_on_cpap"
                  v-model="noteForm.notes.admitted_on_cpap"
                  :placeholder="$t('incubator.admitted-on-cpap')"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.fever')" label-for="fever">
                <b-form-input
                  id="fever"
                  v-model="noteForm.notes.fever"
                  :placeholder="$t('incubator.fever')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.apgar')" label-for="apgar">
                <b-form-input
                  id="apgar"
                  v-model="noteForm.notes.apgar"
                  :placeholder="$t('incubator.apgar')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group :label="$t('incubator.uti')" label-for="uti">
                <b-form-input
                  id="uti"
                  v-model="noteForm.notes.uti"
                  :placeholder="$t('incubator.uti')"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6" cols="12">
              <b-form-group
                :label="$t('incubator.previous-admission-to-nicu')"
                label-for="previous_admission_to_nicu"
              >
                <b-form-input
                  id="previous_admission_to_nicu"
                  v-model="noteForm.notes.previous_admission_to_nicu"
                  :placeholder="$t('incubator.previous-admission-to-nicu')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group
                :label="$t('incubator.mother-blood-g')"
                label-for="mother_blood_g"
              >
                <b-form-input
                  id="mother_blood_g"
                  v-model="noteForm.notes.mother_blood_g"
                  :placeholder="$t('incubator.mother-blood-g')"
                />
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group
                :label="$t('incubator.consanguinty')"
                label-for="consanguinty"
              >
                <b-form-input
                  id="consanguinty"
                  v-model="noteForm.notes.consanguinty"
                  :placeholder="$t('incubator.consanguinty')"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="4" cols="12">
              <b-form-group
                :label="$t('incubator.baby-blood-g')"
                label-for="baby_blood_g"
              >
                <b-form-input
                  id="baby_blood_g"
                  v-model="noteForm.notes.baby_blood_g"
                  :placeholder="$t('incubator.baby-blood-g')"
                />
              </b-form-group>
            </b-col>
            <b-col md="4" cols="6">
              <b-form-group
                :label="$t('incubator.steroid')"
                label-for="steroid"
              >
                <b-form-input
                  id="steroid"
                  v-model="noteForm.notes.steroid"
                  :placeholder="$t('incubator.steroid')"
                />
              </b-form-group>
            </b-col>
            <b-col md="4" cols="6">
              <b-form-group
                :label="$t('incubator.thyrohd-disease')"
                label-for="thyrohd_disease"
              >
                <b-form-input
                  id="thyrohd_disease"
                  v-model="noteForm.notes.thyrohd_disease"
                  :placeholder="$t('incubator.thyrohd-disease')"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <div class="modal-actions">
            <!-- <b-button
              variant="outline-info"
              size="lg"
              :disabled="loadingNote"
              @click="fillTestData"
              class="mr-2"
            >
              <feather-icon icon="TestTubeIcon" size="16" class="mr-1" />
              {{ $t("common.test-data") }}
            </b-button> -->
            <b-button
              variant="outline-secondary"
              size="lg"
              :disabled="loadingNote"
              @click="$bvModal.hide('modal-admission-note')"
            >
              {{ $t("common.cancel") }}
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingNote"
            >
              <span v-if="!loadingNote">{{
                editingNote ? $t("common.update") : $t("common.save")
              }}</span>
              <b-spinner v-else small />
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>

    <!-- Create Invoice Modal -->
    <b-modal
      id="modal-create-invoice"
      :title="$t('incubator.create-new-invoice')"
      size="md"
      hide-footer
      centered
      modal-class="modern-modal"
      :no-close-on-backdrop="true"
    >
      <div class="modal-content-wrapper">
        <div class="patient-info-box">
          <div class="info-row">
            <feather-icon icon="UserIcon" size="16" />
            <div>
              <span class="info-label">{{ $t("reception.patient-name") }}</span>
              <span class="info-value">{{ patientName }}</span>
            </div>
          </div>
          <div class="info-row">
            <feather-icon icon="CpuIcon" size="16" />
            <div>
              <span class="info-label">{{ $t("incubator.incubator") }}</span>
              <span class="info-value">{{ incubatorName }}</span>
            </div>
          </div>
        </div>

        <b-row>
          <b-col md="6" cols="12">
            <b-form-group
              :label="$t('incubator.start-date')"
              label-for="invoice_start_date"
              class="form-group-modern"
            >
              <b-form-input
                id="invoice_start_date"
                v-model="newInvoiceForm.start_date"
                type="date"
                :max="newInvoiceForm.end_date"
                size="lg"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" cols="12">
            <b-form-group
              :label="$t('incubator.end-date')"
              label-for="invoice_end_date"
              class="form-group-modern"
            >
              <b-form-input
                id="invoice_end_date"
                v-model="newInvoiceForm.end_date"
                type="date"
                :min="newInvoiceForm.start_date"
                size="lg"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          :label="$t('incubator.amount')"
          label-for="invoice_amount"
          class="form-group-modern"
        >
          <b-form-input
            id="invoice_amount"
            v-model="newInvoiceForm.amount"
            type="number"
            min="0"
            :placeholder="$t('incubator.amount')"
            size="lg"
            readonly
          />
          <small v-if="calculatedDays > 0" class="text-muted">
            {{ calculatedDays }} {{ $t('common.days') }} × {{ incubatorDailyPrice }} = {{ newInvoiceForm.amount }} IQD
          </small>
          <p v-if="newInvoiceError.amount" class="error-text">
            {{ newInvoiceError.amount }}
          </p>
        </b-form-group>

        <b-form-group
          :label="$t('common.description')"
          label-for="invoice_description"
          class="form-group-modern"
        >
          <b-form-textarea
            id="invoice_description"
            v-model="newInvoiceForm.description"
            :placeholder="$t('common.description')"
            rows="4"
            size="lg"
          />
        </b-form-group>

        <div class="modal-actions">
          <b-button
            variant="outline-secondary"
            size="lg"
            :disabled="loadingInvoice"
            @click="$bvModal.hide('modal-create-invoice')"
          >
            {{ $t("common.cancel") }}
          </b-button>
          <b-button
            variant="success"
            size="lg"
            :disabled="loadingInvoice"
            @click="createNewInvoice"
          >
            <span v-if="!loadingInvoice">{{
              $t("incubator.create-invoice")
            }}</span>
            <b-spinner v-else small />
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Hidden Printable Component -->
    <div v-if="currentNoteForPrint" class="print-only">
      <AdmissionNotePrint :note="currentNoteForPrint" :patient-data="model" />
    </div>

    <!-- Hidden print button for admission notes -->
    <b-button
      ref="admissionNotePrintRef"
      v-print="AdmissionNotePrintConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <!-- Print Invoice Component -->
    <NICUInvoice
      v-if="currentInvoiceForPrint"
      :patient-data="model ? model.patient_card : {}"
      :data="currentInvoiceForPrint"
      :incubator-name="incubatorName"
    />

    <!-- Print Modal Confirmation -->
    <b-modal
      v-for="item in invoices"
      :id="`modal-confirm-print-${item.id}`"
      :key="`print-modal-${item.id}`"
      :no-close-on-backdrop="true"
      :title="$t('incubator.print-nicu-invoice')"
      ok-only
      :ok-title="$t('common.print')"
      @ok="printInvoice"
    >
      <b-card-text>
        <h5>{{ $t("incubator.print-nicu-invoice") }}</h5>
        {{ $t("surgery.print-sure") }}
      </b-card-text>
    </b-modal>

    <!-- Hidden print button for invoices -->
    <b-button
      ref="NICUInvoiceRef"
      v-print="NICUInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <!-- Birth Certificate Commitment Print Component -->
    <div
      v-if="model"
      class="print-only"
      id="birth-certificate-commitment-print"
    >
      <BirthCertificateCommitmentPrint
        ref="birthCertificateCommitmentPrintRef"
        :patient-data="model ? model.patient_card : {}"
      />
    </div>

    <!-- NICU Discharge Against Advice Print Component -->
    <div
      v-if="model"
      class="print-only"
      id="nicu-discharge-against-advice-print"
    >
      <NICUDischargeAgainstAdvicePrint
        ref="nicuDischargeAgainstAdvicePrintRef"
        :patient-data="model ? model.patient_card : {}"
      />
    </div>

    <!-- Add Lab Test Modal -->
    <b-modal
      id="modal-add-lab-test"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.add-lab-test')"
      :ok-disabled="!canSubmitLab"
      @ok="submitLab"
    >
      <b-card-text>
        <b-row>
          <b-col md="6" cols="12">
            <AutoComplete
              v-model="labCreate.singleTests"
              :label="$t('lab.test')"
              :placeholder="$t('lab.test')"
              :multiple="true"
              param-query="search"
              :serializer="labTestSerializer"
              endpoint="test"
              :close-on-select="false"
              text-title="name"
              @input="addTestsToList"
            />
          </b-col>
          <b-col md="6" cols="12">
            <AutoComplete
              v-model="labCreate.group"
              :label="$t('lab.test-group')"
              :placeholder="$t('lab.test-group')"
              param-query="search"
              :serializer="(val) => val.id"
              endpoint="group"
              text-title="name"
              @input="selectGroup"
            />
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('lab.sample-id')"
              label-for="sample-id"
              label-class="font-weight-bold"
            >
              <b-form-input
                id="sample-id"
                v-model="labCreate.sampleId"
                :placeholder="$t('lab.enter-sample-id')"
                trim
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              :label="$t('lab.sample-date')"
              label-for="sample-date"
              label-class="font-weight-bold"
            >
              <b-form-datepicker
                id="sample-date"
                v-model="labCreate.sampleDate"
                :max="new Date().toISOString().slice(0, 10)"
                :placeholder="$t('lab.select-date')"
                today-button
                reset-button
                close-button
                class="mb-2"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Selected Tests Section -->
        <div class="selected-tests-section mt-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">
              {{ $t("lab.test") }}
            </h5>
            <b-button
              v-if="labTestListFiltered.length > 0"
              size="sm"
              variant="outline-danger"
              @click="clearAllTests"
            >
              <feather-icon icon="TrashIcon" size="14" />
              {{ $t("lab.clear-all") }}
            </b-button>
          </div>
          <div class="selected-tests-container">
            <div v-if="!labTestListFiltered.length" class="text-center py-4">
              <feather-icon
                icon="TestTubeIcon"
                size="48"
                class="text-muted mb-2"
              />
              <p class="text-muted mb-0">
                {{ $t("lab.no-tests-selected-message") }}
              </p>
            </div>
            <b-table
              v-else
              :items="labTestListFiltered"
              :fields="labTestColumns"
              striped
              hover
              responsive
              show-empty
              :empty-text="$t('lab.no-tests-selected')"
              class="mb-0"
              thead-class="bg-light"
            >
              <template #cell(name)="data">
                <span class="font-weight-medium">{{ data.item.name }}</span>
              </template>
              <template #cell(price)="data">
                <b-badge variant="light" class="text-dark">
                  {{ formatPrice(data.item.price) }}
                </b-badge>
              </template>
            </b-table>
          </div>
          <!-- Summary Section -->
          <div
            v-if="labTestListFiltered.length > 0"
            class="d-flex justify-content-between mt-2 p-2 bg-light rounded"
          >
            <span class="font-weight-bold"
              >{{ $t("lab.total-tests") }}:
              {{ labTestListFiltered.length }}</span
            >
            <span class="font-weight-bold"
              >{{ $t("lab.total-price") }}:
              {{ formatPrice(labTotalPrice) }}</span
            >
          </div>
        </div>
      </b-card-text>
    </b-modal>

    <!-- Add Drug Modal -->
    <b-modal
      id="modal-add-drug"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.add-drug')"
      :ok-disabled="!canSubmitDrug"
      @ok="submitDrug"
    >
      <b-card-text>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('office.source_of_drug')">
              <v-select
                v-model="drugCreate.source_of_drug"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="[
                  { label: 'Hospital', value: 'hospital' },
                  { label: 'Pharmacy', value: 'pharmacy' },
                  { label: 'External', value: 'external' },
                ]"
                :reduce="(option) => option.value"
                :placeholder="$t('office.source_of_drug')"
                @input="handleDrugSourceChange"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="
              drugCreate.source_of_drug !== 'external' &&
              drugCreate.source_of_drug === 'pharmacy'
            "
            md="6"
          >
            <AutoComplete
              v-model="drugCreate.drug_scientific_name_id"
              :label="$t('common.drug_scientific_name')"
              :placeholder="$t('common.drug_scientific_name')"
              :serializer="(val) => val.id"
              endpoint="drugScientificName"
              text-title="name"
            />
            <AutoComplete
              v-model="drugCreate.item_id"
              :disabled="!drugCreate.source_of_drug"
              :label="$t('office.item_name')"
              :placeholder="$t('office.item_name')"
              :serializer="(val) => val.id"
              endpoint="item"
              text-title="name"
              :other-query="`filter[type]=${drugCreate.source_of_drug}&filter[drug_scientific_name_id]=${drugCreate.drug_scientific_name_id}`"
            />
          </b-col>
          <b-col
            v-if="
              drugCreate.source_of_drug !== 'external' &&
              drugCreate.source_of_drug === 'hospital'
            "
            md="6"
          >
            <AutoComplete
              v-model="drugCreate.item_id"
              :disabled="!drugCreate.source_of_drug"
              :label="$t('office.item_name')"
              :placeholder="$t('office.item_name')"
              :serializer="(val) => val.id"
              endpoint="item"
              text-title="name"
              :other-query="`filter[type]=${drugCreate.source_of_drug}`"
            />
          </b-col>
          <b-col v-if="drugCreate.source_of_drug === 'external'" md="6">
            <b-form-group :label="$t('office.item_name_external')">
              <b-form-input
                v-model="drugCreate.item_name"
                :placeholder="$t('office.item_name_external')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('office.number_of_doses')">
              <b-form-input
                v-model="drugCreate.number_of_doses"
                type="number"
                :placeholder="$t('office.number_of_doses')"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('office.duration_between_doses')">
              <b-form-input
                v-model="drugCreate.duration_between_doses"
                :placeholder="$t('office.duration_between_doses')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('office.type')">
              <b-form-input
                v-model="drugCreate.type"
                :placeholder="$t('office.type')"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('office.dose')">
              <b-form-input
                v-model="drugCreate.dose"
                :placeholder="$t('office.dose')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group :label="$t('office.start_time')">
              <datetime
                v-model="drugCreate.start_time"
                input-class="form-control"
                type="datetime"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group :label="$t('office.description')">
              <b-form-textarea
                v-model="drugCreate.description"
                :placeholder="$t('office.description')"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <!-- Add Radiology Modal -->
    <b-modal
      id="modal-add-radiology"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('reception.add-radiology')"
      :ok-disabled="!canSubmitRadiology"
      @ok="submitRadiology"
    >
      <b-card-text>
        <b-row>
          <b-col md="6">
            <AutoComplete
              v-model="radiologyCreate.radiology_id"
              :label="$t('radiology.radiology')"
              :placeholder="$t('radiology.radiology')"
              param-query="search"
              :serializer="(val) => val.id"
              endpoint="radiology"
              text-title="name"
            />
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('common.url')" label-for="url">
              <b-form-input
                id="url"
                v-model="radiologyCreate.url"
                :placeholder="$t('common.url')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BSpinner,
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
  BCol,
  BForm,
  BModal,
  BCardText,
  BTable,
  BFormDatepicker,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue";
import permissionChecker from "@/mixins/permissionChecker";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { getNestedProperty } from "@/utils/nestedProperty";
import AdmissionNotePrint from "@/components/printables/AdmissionNote.vue";
import InvoiceCard from "@/components/invoices/InvoiceCard.vue";
import vSelect from "vue-select";
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";

const NICUInvoice = () => import("@/components/printables/NICUInvoice.vue");
const AutoComplete = () => import("@/components/common/AutoComplete.vue");
const BirthCertificateCommitmentPrint = () =>
  import("@/components/printables/BirthCertificateCommitmentPrint.vue");
const NICUDischargeAgainstAdvicePrint = () =>
  import("@/components/printables/NICUDischargeAgainstAdvicePrint.vue");

export default {
  name: "PatientIncubatorShow",
  components: {
    BBadge,
    BSpinner,
    BAlert,
    BButton,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BForm,
    BModal,
    BCardText,
    BTable,
    BFormDatepicker,
    BDropdown,
    BDropdownItem,
    AdmissionNotePrint,
    NICUInvoice,
    InvoiceCard,
    vSelect,
    Datetime,
    AutoComplete,
    BirthCertificateCommitmentPrint,
    NICUDischargeAgainstAdvicePrint,
  },
  mixins: [permissionChecker],
  data() {
    return {
      id: this.$route.params.id,
      loading: true,
      error: "",
      model: null,
      invoices: [],
      admissionNotes: [],
      newInvoiceForm: { 
        amount: "", 
        description: "", 
        type: "invoice_nicu",
        start_date: "",
        end_date: ""
      },
      newInvoiceError: { amount: "" },
      loadingInvoice: false,
      AdmissionNotePrintConfig: {
        id: "printable-admission-note",
      },
      noteForm: {
        datetime: "",
        notes: {
          patient_name: "",
          tob: "",
          toa: "",
          dob: "",
          doa: "",
          wt: "",
          referro_from: "",
          pediatrician_s: "",
          pediatrician_p: "",
          cc: "",
          ght: "",
          gdm: "",
          ga: "",
          g: "",
          p: "",
          a: "",
          brief_note: "",
          aph: "",
          ll: "",
          poly_oligo: "",
          admitted_on_cpap: "",
          fever: "",
          apgar: "",
          uti: "",
          previous_admission_to_nicu: "",
          mother_blood_g: "",
          consanguinty: "",
          baby_blood_g: "",
          steroid: "",
          thyrohd_disease: "",
        },
      },
      currentNoteForPrint: null,
      noteErrors: { datetime: "" },
      loadingNote: false,
      editingNote: null,
      currentInvoiceForPrint: null,
      NICUInvoiceConfig: {
        id: "NICUInvoice",
      },
      birthCertificateCommitmentRef: null,
      labCreate: {
        singleTests: [],
        group: null,
        sampleId: "",
        sampleDate: new Date().toISOString().slice(0, 10),
        lastSelectedGroupId: null,
        testsList: [],
      },
      labTestColumns: [
        { key: "name", label: this.$t("lab.test-name") },
        { key: "price", label: this.$t("common.price") },
      ],
      drugCreate: {
        item_id: null,
        item_name: "",
        number_of_doses: "",
        duration_between_doses: "",
        source_of_drug: "",
        type: "",
        dose: "",
        start_time: new Date().toISOString(),
        description: "",
        drug_scientific_name_id: null,
      },
      radiologyCreate: { radiology_id: null, type: "1", url: "" },
      radiologyPatientData: [],
      radiologyData: [],
    };
  },
  computed: {
    canUpdate() {
      return true;
    },
    patientCardId() {
      return this.model?.patient_card_id || "";
    },
    patientCardNo() {
      return this.model?.patient_card?.card_no || "";
    },
    patientNo() {
      return this.model?.patient_card?.patient?.patient_no || "";
    },
    patientName() {
      return (
        this.model?.patient_card?.patient?.name ||
        this.$t("common.not-available")
      );
    },
    motherName() {
      return this.model?.patient_card?.patient?.mother_name || "";
    },
    childName() {
      const name = this.model?.patient_card?.child_name || "";
      return name && name !== "ابن" && name !== "بنت" ? name : "";
    },
    phone() {
      return this.model?.patient_card?.patient?.phone || "";
    },
    gender() {
      return this.model?.patient_card?.patient?.gender || "";
    },
    incubatorName() {
      return this.model?.incubator?.name || "N/A";
    },
    incubatorBrand() {
      return this.model?.incubator?.brand || "";
    },
    incubatorType() {
      return this.model?.incubator?.incubator_type?.name || "";
    },
    incubatorDailyPrice() {
      return Number(this.model?.incubator?.incubator_type?.price || 0);
    },
    calculatedDays() {
      if (!this.newInvoiceForm.start_date || !this.newInvoiceForm.end_date) {
        return 0;
      }
      const start = new Date(this.newInvoiceForm.start_date);
      const end = new Date(this.newInvoiceForm.end_date);
      
      if (isNaN(start) || isNaN(end) || end < start) {
        return 0;
      }
      
      const diffMs = end - start;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
      
      return diffDays > 0 ? diffDays : 0;
    },
    labTestListFiltered() {
      const uniqueMap = new Map();
      this.labCreate.testsList.forEach((test) => {
        const id = test.test_id || test.id;
        if (id) {
          uniqueMap.set(id, test);
        }
      });
      return Array.from(uniqueMap.values());
    },
    labTotalPrice() {
      return this.labTestListFiltered.reduce(
        (sum, test) => sum + Number(test.price || 0),
        0
      );
    },
    canSubmitLab() {
      return this.labTestListFiltered.length > 0 && this.model?.is_active;
    },
    canSubmitDrug() {
      return (
        this.drugCreate.source_of_drug &&
        ((this.drugCreate.source_of_drug === "external" &&
          this.drugCreate.item_name) ||
          (this.drugCreate.source_of_drug !== "external" &&
            this.drugCreate.item_id)) &&
        this.drugCreate.number_of_doses &&
        this.drugCreate.duration_between_doses &&
        this.model?.is_active
      );
    },
    canSubmitRadiology() {
      return this.radiologyCreate.radiology_id && this.model?.is_active;
    },
  },
  watch: {
    "$route.params.id": function (val) {
      this.id = val;
      this.fetch();
    },
    "newInvoiceForm.start_date": function () {
      this.calculateInvoiceAmount();
    },
    "newInvoiceForm.end_date": function () {
      this.calculateInvoiceAmount();
    },
    "labCreate.singleTests": {
      handler(newVal, oldVal) {
        if (!oldVal) return;
        if (newVal.length < oldVal.length) {
          const newIds = newVal.map((item) => item.test_id || item.id);
          const removedItems = oldVal.filter(
            (item) => !newIds.includes(item.test_id || item.id)
          );
          removedItems.forEach((removedItem) => {
            this.labCreate.testsList = this.labCreate.testsList.filter(
              (test) => test.test_id !== (removedItem.test_id || removedItem.id)
            );
          });
        }
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
    this.getRadiologyData();
  },
  methods: {
    getNestedProperty,

    async fetch() {
      if (this._fetching) return;
      this._fetching = true;

      this.loading = true;
      this.error = "";
      this.model = null;
      this.invoices = [];
      this.admissionNotes = [];

      try {
        const url = `/patientCardIncubators/${this.id}?include=patientCard,incubator`;
        await this.getList(url);

        const root = this.all_data || null;
        const model =
          root && typeof root === "object"
            ? root.data && typeof root.data === "object"
              ? root.data
              : root
            : null;

        if (!model || !model.id) {
          this.error = this.$t("common.no_results");
          return;
        }

        this.model = model;

        // Fetch invoices, admission notes, and radiology data
        await Promise.all([
          this.fetchInvoices(),
          this.fetchAdmissionNotes(),
          this.getRadiologyData(),
        ]);
      } catch (e) {
        const status = e?.response?.status;
        this.error =
          status === 401 || status === 403
            ? this.$t("common.no_permission")
            : this.$t("common.error-occurred");
      } finally {
        this.loading = false;
        this._fetching = false;
      }
    },

    async fetchInvoices() {
      if (!this.model || !this.model.patient_card_id) {
        this.invoices = [];
        return;
      }

      try {
        const patientCardId = this.model.patient_card_id;
        const url = `/invoices?filter[patient_card_id]=${patientCardId}&page=1&take=20&sort=-id&include=service,surgery,patientCard,doctor,invoiceItems,patientCardIncubator,patientCardIncubator.incubator`;
        await this.getList(url);

        this.invoices = Array.isArray(getNestedProperty(this.all_data, "data"))
          ? getNestedProperty(this.all_data, "data")
          : [];
      } catch (e) {
        console.error("Error fetching invoices:", e);
        this.invoices = [];
      }
    },

    async fetchAdmissionNotes() {
      try {
        const url = `/patientCardAdmissionNotes?filter[patient_card_incubator_id]=${this.id}&sort=-datetime`;
        await this.getList(url);
        this.admissionNotes = Array.isArray(
          getNestedProperty(this.all_data, "data")
        )
          ? getNestedProperty(this.all_data, "data")
          : [];
      } catch (e) {
        console.error("Error fetching admission notes:", e);
        this.admissionNotes = [];
      }
    },

    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "—";

      // Handle both formats: "2025-10-18 03:16:00 pm" and standard ISO
      let d;
      if (dateTime.includes("am") || dateTime.includes("pm")) {
        // Parse custom format: "2025-10-18 03:16:00 pm"
        const parts = dateTime.split(" ");
        const datePart = parts[0];
        const timePart = parts[1];
        const ampm = parts[2];

        const [year, month, day] = datePart.split("-");
        let [hours, minutes] = timePart.split(":");
        hours = parseInt(hours);

        if (ampm === "pm" && hours !== 12) hours += 12;
        if (ampm === "am" && hours === 12) hours = 0;

        d = new Date(year, month - 1, day, hours, minutes);
      } else {
        d = new Date(dateTime);
      }

      if (isNaN(d)) return dateTime;

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;

      return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
    },

    calculateDuration(startTime, endTime) {
      if (!startTime || !endTime) return "—";

      const start = new Date(startTime);
      const end = new Date(endTime);

      if (isNaN(start) || isNaN(end)) return "—";

      const diffMs = end - start;
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      return `${diffDays} ${this.$t("common.days")}`;
    },

    isPaymentExpired(item) {
      const paidUntil = item?.paid_until;
      if (!paidUntil) return false;
      const paidUntilDate = new Date(paidUntil);
      const now = new Date();
      return now >= paidUntilDate;
    },

    getTimeRemaining(item) {
      const paidUntil = item?.paid_until;
      if (!paidUntil) return this.$t("common.not-available");
      const paidUntilDate = new Date(paidUntil);
      const now = new Date();
      const diffMs = paidUntilDate - now;

      if (diffMs <= 0) {
        const overdueDays = Math.ceil(Math.abs(diffMs) / (1000 * 60 * 60 * 24));
        return `${overdueDays}d ${this.$t("incubator.overdue_label")}`;
      }
      
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return `${diffDays}d ${this.$t("incubator.left_label")}`;
    },

    getPaymentTimerClass(item) {
      return this.isPaymentExpired(item)
        ? "clock-icon-expired"
        : "clock-icon-active";
    },

    getInvoiceStatusVariant(status) {
      const map = {
        paid: "success",
        pending: "warning",
        cancelled: "danger",
        partial: "info",
      };
      return map[status] || "secondary";
    },

    formatInvoiceType(type) {
      return type === "invoice_nicu" ? "NICU" : type;
    },

    openCreateInvoiceModal() {
      // Set start date to today or paid_until date
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      // Default end date to one day after start
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      
      this.newInvoiceForm = {
        amount: "",
        description: "",
        type: "invoice_nicu",
        start_date: todayStr,
        end_date: tomorrowStr,
      };
      this.newInvoiceError.amount = "";
      
      // Calculate initial amount
      this.$nextTick(() => {
        this.calculateInvoiceAmount();
      });
      
      this.$bvModal.show("modal-create-invoice");
    },

    calculateInvoiceAmount() {
      if (this.calculatedDays > 0 && this.incubatorDailyPrice > 0) {
        this.newInvoiceForm.amount = this.calculatedDays * this.incubatorDailyPrice;
      } else {
        this.newInvoiceForm.amount = "";
      }
    },

    async createNewInvoice() {
      this.loadingInvoice = true;
      this.newInvoiceError.amount = "";

      if (!this.newInvoiceForm.amount || this.newInvoiceForm.amount <= 0) {
        this.newInvoiceError.amount = this.$t("validation.required");
        this.loadingInvoice = false;
        return;
      }

      try {
        const payload = {
          invoices: [
            {
              patient_card_id: this.model.patient_card_id,
              patient_card_incubator_id: this.model.id,
              amount: parseFloat(this.newInvoiceForm.amount),
              type: this.newInvoiceForm.type,
              ...(this.newInvoiceForm.description && {
                description: this.newInvoiceForm.description,
              }),
            },
          ],
        };

        await this.$store.dispatch("create", {
          form: payload,
          path: "/invoices",
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("incubator.invoice-created-successfully"),
          },
        });

        this.$bvModal.hide("modal-create-invoice");
        await this.fetchInvoices();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("common.error-occurred"),
          },
        });
      } finally {
        this.loadingInvoice = false;
      }
    },

    openCheckoutConfirm() {
      this.$bvModal
        .msgBoxConfirm(this.$t("incubator.checkout_confirm_text"), {
          title: this.$t("incubator.checkout_confirm_title"),
          size: "md",
          okVariant: "warning",
          okTitle: this.$t("incubator.checkout"),
          cancelTitle: this.$t("common.cancel"),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then(async (value) => {
          if (!value) return;

          await this.$store.dispatch("update", {
            form: { _method: "PUT" },
            path: `/patientCardIncubators/${this.id}/checkout`,
          });

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.success"),
              variant: "success",
              text: this.$t("incubator.checkout_success"),
            },
          });

          await this.fetch();
        })
        .catch(() => {});
    },

    formatPatientName(item) {
      const parentName = item?.patient_card?.patient?.name || "N/A";
      const childName = item?.patient_card?.child_name || "";

      if (!childName) return parentName;
      if (childName === "ابن" || childName === "بنت")
        return `${childName} ${parentName}`;
      return `${childName} (${parentName})`;
    },

    getNoteValue(note, field) {
      if (!note || !note.notes) return "";
      const notesData =
        typeof note.notes === "string" ? JSON.parse(note.notes) : note.notes;

      // Handle array structure - get the first item if it's an array
      const data = Array.isArray(notesData) ? notesData[0] : notesData;

      return data[field] || "";
    },

    openAdmissionNoteModal(note) {
      if (note) {
        this.editingNote = note;
        const notesData =
          typeof note.notes === "string" ? JSON.parse(note.notes) : note.notes;

        // Handle array structure - get the first item if it's an array
        const data = Array.isArray(notesData) ? notesData[0] : notesData;

        // Format datetime for input
        let datetimeValue = note.datetime || "";
        if (datetimeValue) {
          const d = new Date(datetimeValue);
          if (!isNaN(d)) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            const hours = String(d.getHours()).padStart(2, "0");
            const minutes = String(d.getMinutes()).padStart(2, "0");
            datetimeValue = `${year}-${month}-${day}T${hours}:${minutes}`;
          }
        }

        this.noteForm = {
          datetime: datetimeValue,
          notes: {
            patient_name: data.patient_name || "",
            age: data.age || "",
            tob: data.tob || "",
            toa: data.toa || "",
            dob: data.dob || "",
            doa: data.doa || "",
            wt: data.wt || "",
            referro_from: data.referro_from || "",
            pediatrician_s: data.pediatrician_s || "",
            pediatrician_p: data.pediatrician_p || "",
            cc: data.cc || "",
            ght: data.ght || "",
            gdm: data.gdm || "",
            ga: data.ga || "",
            g: data.g || "",
            p: data.p || "",
            a: data.a || "",
            brief_note: data.brief_note || "",
            aph: data.aph || "",
            ll: data.ll || "",
            poly_oligo: data.poly_oligo || "",
            admitted_on_cpap: data.admitted_on_cpap || "",
            fever: data.fever || "",
            apgar: data.apgar || "",
            uti: data.uti || "",
            previous_admission_to_nicu: data.previous_admission_to_nicu || "",
            mother_blood_g: data.mother_blood_g || "",
            consanguinty: data.consanguinty || "",
            baby_blood_g: data.baby_blood_g || "",
            steroid: data.steroid || "",
            thyrohd_disease: data.thyrohd_disease || "",
          },
        };
      } else {
        this.editingNote = null;
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");

        // Auto-fill patient name
        const autoPatientName = this.formatPatientName(this.model);

        this.noteForm = {
          datetime: `${year}-${month}-${day}T${hours}:${minutes}`,
          notes: {
            patient_name: autoPatientName,
            age: "",
            tob: "",
            toa: "",
            dob: "",
            doa: "",
            wt: "",
            referro_from: "",
            pediatrician_s: "",
            pediatrician_p: "",
            cc: "",
            ght: "",
            gdm: "",
            ga: "",
            g: "",
            p: "",
            a: "",
            brief_note: "",
            aph: "",
            ll: "",
            poly_oligo: "",
            admitted_on_cpap: "",
            fever: "",
            apgar: "",
            uti: "",
            previous_admission_to_nicu: "",
            mother_blood_g: "",
            consanguinty: "",
            baby_blood_g: "",
            steroid: "",
            thyrohd_disease: "",
          },
        };
      }
      this.noteErrors.datetime = "";
      this.$bvModal.show("modal-admission-note");
    },

    async saveAdmissionNote() {
      this.loadingNote = true;
      this.noteErrors.datetime = "";

      if (!this.noteForm.datetime) {
        this.noteErrors.datetime = this.$t("validation.required");
        this.loadingNote = false;
        return;
      }

      try {
        // Convert datetime-local to proper format
        const datetimeInput = this.noteForm.datetime;
        const d = new Date(datetimeInput);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const seconds = "00";
        const formattedDatetime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

        if (this.editingNote) {
          // For update: API expects notes as JSON string
          const updatePayload = {
            patient_card_incubator_id: this.id,
            datetime: formattedDatetime,
            notes: JSON.stringify([this.noteForm.notes]),
            _method: "PUT",
          };

          await this.$store.dispatch("update", {
            form: updatePayload,
            path: `/patientCardAdmissionNotes/${this.editingNote.id}`,
          });

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.success"),
              variant: "success",
              text: this.$t("incubator.admission-note-updated"),
            },
          });
        } else {
          // For create: API expects notes as array
          const createPayload = {
            patient_card_incubator_id: this.id,
            datetime: formattedDatetime,
            notes: [this.noteForm.notes],
          };

          await this.$store.dispatch("create", {
            form: createPayload,
            path: "/patientCardAdmissionNotes",
          });

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.success"),
              variant: "success",
              text: this.$t("incubator.admission-note-created"),
            },
          });
        }

        this.$bvModal.hide("modal-admission-note");
        await this.fetchAdmissionNotes();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("common.error-occurred"),
          },
        });
      } finally {
        this.loadingNote = false;
      }
    },

    async deleteAdmissionNote(noteId) {
      this.$bvModal
        .msgBoxConfirm(this.$t("common.delete-confirmation"), {
          title: this.$t("common.confirm"),
          size: "md",
          okVariant: "danger",
          okTitle: this.$t("common.delete"),
          cancelTitle: this.$t("common.cancel"),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        })
        .then(async (value) => {
          if (!value) return;

          await this.$store.dispatch(
            "delete",
            `/patientCardAdmissionNotes/${noteId}`
          );

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.success"),
              variant: "success",
              text: this.$t("incubator.admission-note-deleted"),
            },
          });

          await this.fetchAdmissionNotes();
        })
        .catch(() => {});
    },

    printAdmissionNote(note) {
      this.currentNoteForPrint = note;
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.admissionNotePrintRef.click();
        }, 100);
      });
    },

    printBirthCertificateCommitment() {
      this.$nextTick(() => {
        if (this.$refs.birthCertificateCommitmentPrintRef) {
          this.$refs.birthCertificateCommitmentPrintRef.print();
        }
      });
    },

    printNICUDischargeAgainstAdvice() {
      this.$nextTick(() => {
        if (this.$refs.nicuDischargeAgainstAdvicePrintRef) {
          this.$refs.nicuDischargeAgainstAdvicePrintRef.print();
        }
      });
    },

    showNICUInvoice(item) {
      this.currentInvoiceForPrint = item;
      this.$bvModal.show(`modal-confirm-print-${item.id}`);
    },

    async printInvoice() {
      try {
        this.loading = true;

        const url = `/invoices?filter[id]=${this.currentInvoiceForPrint.id}&include=patientCard,patientCardIncubator,patientCardIncubator.incubator`;
        await this.getList(url);

        if (
          this.all_data &&
          this.all_data.data &&
          this.all_data.data.length > 0
        ) {
          this.currentInvoiceForPrint = this.all_data.data[0];

          await this.$nextTick();

          setTimeout(() => {
            this.$refs.NICUInvoiceRef.click();
            this.changeStatusToPrintedInvoice();
          }, 100);
        }
      } catch (error) {
        console.error("Failed to print invoice:", error);
      } finally {
        this.loading = false;
      }
    },

    changeStatusToPrintedInvoice() {
      const params = {
        form: { status: "printed" },
        path: `/invoices/${this.currentInvoiceForPrint.id}/changeStatus`,
      };
      this.$store
        .dispatch("put", params)
        .then((response) => {
          this.fetchInvoices();
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: `${this.$t("common.success")}`,
              variant: "success",
              text: `${response.data.message}`,
            },
          });
        })
        .catch((error) => {
          if (error.response.status === 400) {
            for (const item in error.response.data) {
              if (error.response.data.hasOwnProperty(item)) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          }
        });
    },

    fillTestData() {
      // Set current datetime
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");

      this.noteForm.datetime = `${year}-${month}-${day}T${hours}:${minutes}`;

      // Fill with test data
      this.noteForm.notes = {
        patient_name: this.formatPatientName(this.model) || "Test Patient Name",
        tob: "14:30",
        toa: "15:45",
        dob: "2024-01-15",
        doa: "2024-01-20",
        wt: "2.5 kg",
        referro_from: "Al-Kindi Hospital",
        pediatrician_s: "Dr. Ahmed Ali",
        pediatrician_p: "Dr. Sarah Mohammed",
        cc: "Respiratory distress, prematurity",
        ght: "No",
        gdm: "No",
        ga: "32 weeks",
        g: "2",
        p: "1",
        a: "0",
        brief_note:
          "Premature baby with respiratory distress. Requires NICU care and monitoring.",
        aph: "No",
        ll: "Normal",
        poly_oligo: "Normal",
        admitted_on_cpap: "CPAP support, 5cm H2O",
        fever: "No",
        apgar: "7/8",
        uti: "No",
        previous_admission_to_nicu: "No",
        mother_blood_g: "O+",
        consanguinty: "No",
        baby_blood_g: "O+",
        steroid: "Yes, 2 doses",
        thyrohd_disease: "No",
      };

      this.$toast({
        component: ToastificationContent,
        position: "top-right",
        props: {
          title: this.$t("common.success"),
          variant: "success",
          text: this.$t("common.test-data-filled"),
        },
      });
    },

    // Lab Test Methods
    labTestSerializer(val) {
      return {
        name: val.name,
        test_id: val.id,
        price: val.price,
      };
    },
    clearAllTests() {
      this.labCreate.testsList = [];
      this.labCreate.singleTests = [];
      this.labCreate.group = null;
      this.labCreate.sampleId = "";
      this.labCreate.sampleDate = new Date().toISOString().slice(0, 10);
    },
    addTestsToList(e) {
      if (!e || !e.length) return;
      const newTestsList = [...this.labCreate.testsList];
      const existingIds = new Set();
      newTestsList.forEach((test) => {
        const id = test.test_id || test.id;
        if (id) existingIds.add(id);
      });
      const testsToAdd = [];
      e.forEach((newTest) => {
        const testId = newTest.test_id || newTest.id;
        if (testId && !existingIds.has(testId)) {
          const test = {
            test_id: testId,
            name: newTest.name,
            price: newTest.price || 0,
          };
          testsToAdd.push(test);
          existingIds.add(testId);
        }
      });
      if (testsToAdd.length > 0) {
        this.labCreate.testsList = [...newTestsList, ...testsToAdd];
      }
    },
    async selectGroup(e) {
      try {
        if (this.labCreate.lastSelectedGroupId !== null) {
          this.labCreate.testsList = this.labCreate.testsList.filter(
            (test) => test.group_id !== this.labCreate.lastSelectedGroupId
          );
        }
        this.labCreate.lastSelectedGroupId = e;
        await this.getPatient(`/testGroup?filter[group_id]=${e}&include=test`);
        const groupTests = this.all_data?.data || [];
        if (groupTests.length === 0) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "No tests found in this group",
              variant: "warning",
            },
          });
        } else {
          const mappedTests = groupTests.map((test) => ({
            group_id: e,
            price: test.test.price,
            test_id: test.test.id,
            name: test.test.name,
          }));
          const existingTestIds = new Set(
            this.labCreate.testsList.map((test) => test.test_id)
          );
          const newGroupTests = mappedTests.filter(
            (newTest) => !existingTestIds.has(newTest.test_id)
          );
          if (newGroupTests.length > 0) {
            this.labCreate.testsList = [
              ...this.labCreate.testsList,
              ...newGroupTests,
            ];
          }
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error fetching test group",
            variant: "danger",
          },
        });
      }
    },
    async submitLab() {
      if (this.labTestListFiltered.length === 0) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Please select at least one test",
            variant: "warning",
          },
        });
        return;
      }
      try {
        const finalData = {
          patient_card_id: this.patientCardId,
          tests: this.labTestListFiltered.map((test) => {
            if (test.group_id) {
              delete test.group_id;
            }
            return test;
          }),
          sample_id: this.labCreate.sampleId,
        };
        if (this.labCreate.sampleDate) {
          finalData.date = this.labCreate.sampleDate;
        }
        await this.$store.dispatch("create", {
          form: finalData,
          path: "/patientCardTest",
        });
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Tests added successfully",
            variant: "success",
          },
        });
        this.clearAllTests();
        this.$bvModal.hide("modal-add-lab-test");
        // Refetch invoices after adding lab test
        await this.fetchInvoices();
      } catch (error) {
        if (error.response?.status === 400) {
          const errors = error.response.data;
          for (const item in errors) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: errors[item][0],
              },
            });
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error adding tests",
              variant: "danger",
            },
          });
        }
      }
    },

    // Drug Methods
    handleDrugSourceChange(value) {
      this.drugCreate.item_name = "";
      this.drugCreate.item_id = null;
      this.drugCreate.drug_scientific_name_id = null;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      if (!date || isNaN(date)) {
        return "";
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:00`;
    },
    async submitDrug() {
      let count = 0;
      if (!this.drugCreate.source_of_drug) {
        count++;
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: "The source of drug field is required",
          },
        });
      }
      if (this.drugCreate.source_of_drug === "external") {
        if (!this.drugCreate.item_name) {
          count++;
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Validation Error",
              variant: "danger",
              text: "The item name field is required",
            },
          });
        }
      } else {
        if (!this.drugCreate.item_id) {
          count++;
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Validation Error",
              variant: "danger",
              text: "The item field is required",
            },
          });
        }
        if (!this.drugCreate.number_of_doses) {
          count++;
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Validation Error",
              variant: "danger",
              text: "The number of doses field is required",
            },
          });
        }
        if (!this.drugCreate.duration_between_doses) {
          count++;
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Validation Error",
              variant: "danger",
              text: "The duration between doses field is required",
            },
          });
        }
      }
      if (count > 0) {
        return false;
      }
      const payload = {
        item_id:
          this.drugCreate.source_of_drug === "external"
            ? null
            : this.drugCreate.item_id,
        item_name:
          this.drugCreate.source_of_drug === "external"
            ? this.drugCreate.item_name
            : "",
        number_of_doses: this.drugCreate.number_of_doses,
        duration_between_doses: this.drugCreate.duration_between_doses,
        source_of_drug: this.drugCreate.source_of_drug,
        type: this.drugCreate.type,
        dose: this.drugCreate.dose,
        start_time: this.formatDate(this.drugCreate.start_time),
        description: this.drugCreate.description,
        patient_card_id: this.patientCardId,
      };
      try {
        await this.$store.dispatch("create", {
          form: payload,
          path: "/patientCardPrescriptions",
        });
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: { title: this.$t("common.success"), variant: "success" },
        });
        this.drugCreate = {
          item_id: null,
          item_name: "",
          number_of_doses: "",
          duration_between_doses: "",
          source_of_drug: "",
          type: "",
          dose: "",
          start_time: new Date().toISOString(),
          description: "",
          drug_scientific_name_id: null,
        };
        this.$bvModal.hide("modal-add-drug");
        // Refetch invoices after adding drug
        await this.fetchInvoices();
      } catch (error) {
        if (error.response && error.response.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "validation error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          }
        }
      }
    },

    // Radiology Methods
    async submitRadiology() {
      const payload = {
        type: "1",
        url: this.radiologyCreate.url,
        patient_card_id: this.patientCardId,
        radiology_id: this.radiologyCreate.radiology_id,
      };
      try {
        await this.$store.dispatch("create", {
          form: payload,
          path: "/patientCardRadiology",
        });
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: { title: this.$t("common.success"), variant: "success" },
        });
        this.radiologyCreate = { radiology_id: null, type: "1", url: "" };
        this.$bvModal.hide("modal-add-radiology");
        // Refetch invoices after adding radiology
        await this.fetchInvoices();
      } catch (error) {
        if (error.response && error.response.status === 400) {
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "validation error",
                variant: "danger",
                text: error.response.data[item][0],
              },
            });
          }
        }
      }
    },

    // Get Radiology Data
    async getRadiologyData() {
      try {
        if (!this.patientCardId) return;
        
        // Fetch patient radiology records
        await this.getList(
          `patientCardRadiology?take=20&filter[patient_card_id]=${this.patientCardId}&include=attachments`
        );
        this.radiologyPatientData = this.all_data.data || [];

        // Fetch radiology reference data
        await this.getList("radiology");
        this.radiologyData = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching radiology data:", error);
      }
    },

    getRadiologyName(radiologyId) {
      const radiology = this.radiologyData.find(r => r.id === radiologyId);
      return radiology ? radiology.name : "N/A";
    },

    openAttachment(url) {
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
.patient-incubator-detail {
  margin: 0 auto;
  padding: 1.5rem;
}

/* Header */
.page-header {
  margin-bottom: 2rem;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-weight: 500;
  padding: 0.5rem 0;
  margin-bottom: 1rem;
  text-decoration: none;
  transition: all 0.2s;
}

.back-btn:hover {
  color: #3b82f6;
  text-decoration: none;
}

.header-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-title h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-badge {
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  font-weight: 600;
}

.id-badge {
  color: #64748b;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Loading/Error */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;
}

.loading-state p {
  color: #64748b;
  margin: 0;
}

.error-alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-radius: 12px;
  padding: 1.25rem;
}

/* Main Card */
/* Main Card – Updated */
.main-card {
  background: #ffffff; /* White clean surface */
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06); /* Softer shadow */
  border: 1px solid #e2e8f0; /* Light hospital blue-gray */
  color: #0f172a; /* Default dark text instead of white */
}

.main-card .section-icon {
  background: #e0f2fe; /* Soft blue background for icons */
}

.time-status {
  background: #f1f5f9; /* Neutral background */
  color: #0f172a;
}

.time-status.expired {
  background: #fee2e2; /* Light red tone for expired payments */
}

.clock-icon-active {
  animation: none; /* Remove spinning effect */
}

.clock-icon-expired {
  animation: none; /* Remove shaking effect */
}

.card-section {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.section-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.patient-icon {
  background: rgba(255, 255, 255, 0.2);
}

.incubator-icon {
  background: rgba(255, 255, 255, 0.2);
}

.section-content {
  flex: 1;
}

.patient-name {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
}

.patient-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  opacity: 0.95;
}

.incubator-name {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.incubator-brand,
.incubator-type {
  font-size: 0.875rem;
  margin: 0.25rem 0;
  opacity: 0.9;
}

.divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin: 1.5rem 0;
}

.time-status {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
}

.time-status.expired {
  background: rgba(239, 68, 68, 0.2);
}

.time-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.time-content {
  flex: 1;
}

.time-label {
  font-size: 0.875rem;
  opacity: 0.9;
  margin: 0 0 0.25rem 0;
}

.time-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
}

.clock-icon-active {
  animation: spin 4s linear infinite;
}

.clock-icon-expired {
  animation: shake 0.5s ease-in-out infinite;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* Two columns layout */
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
}

.info-card.full-width {
  grid-column: 1 / -1;
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  color: #667eea;
}

.info-card-header h4 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #0f172a;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px dashed #e2e8f0;
}

.info-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-item.vertical {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.info-item.highlight {
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.1) 0%,
    rgba(118, 75, 162, 0.1) 100%
  );
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.2);
}

.info-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.info-value {
  font-size: 0.9375rem;
  color: #0f172a;
  font-weight: 600;
  margin: 0;
}

.medical-text {
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.no-data {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #94a3b8;
  font-size: 0.875rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
}

/* Invoices Section */
.invoices-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #667eea;
}

.section-title h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #0f172a;
}

.count-badge {
  background: #667eea;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
}

.add-invoice-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2);
  transition: all 0.2s;
}

.add-invoice-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.invoices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
}

.invoice-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.25rem;
  transition: all 0.3s;
}

.invoice-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.invoice-number {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.invoice-status {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.invoice-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #22c55e;
  margin-bottom: 0.75rem;
}

.invoice-description {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.75rem;
  line-height: 1.5;
}

.invoice-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.75rem;
  border-top: 1px dashed #cbd5e1;
  gap: 0.5rem;
}

.invoice-date,
.invoice-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
}

.invoice-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px dashed #cbd5e1;
  display: flex;
  justify-content: flex-end;
}

.invoice-actions button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hidden {
  display: none !important;
}

.no-invoices {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  color: #94a3b8;
}

.no-invoices svg {
  opacity: 0.3;
  margin-bottom: 1rem;
}

.no-invoices p {
  margin: 0;
  font-size: 1rem;
}

/* Action Section */
.action-section {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}

.checkout-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2.5rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 1.125rem;
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
  transition: all 0.3s;
}

.checkout-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(251, 191, 36, 0.4);
}

/* Modal Styles */
.modal-content-wrapper {
  padding: 0.5rem 0;
}

.patient-info-box {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.info-row svg {
  color: #667eea;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.info-row div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-row .info-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-row .info-value {
  font-size: 0.9375rem;
  color: #0f172a;
  font-weight: 600;
}

.form-group-modern {
  margin-bottom: 1.5rem;
}

.form-group-modern label {
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.5rem;
}

.form-group-modern input,
.form-group-modern textarea {
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  transition: all 0.2s;
}

.form-group-modern input:focus,
.form-group-modern textarea:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.error-text {
  color: #ef4444;
  font-size: 0.875rem;
  margin: 0.5rem 0 0 0;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.modal-actions button {
  flex: 1;
  border-radius: 10px;
  font-weight: 600;
}

.modal-actions .mr-2 {
  margin-right: 0.5rem;
  flex: 0 0 auto;
  min-width: 140px;
}

/* Animations */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-4px);
  }
  75% {
    transform: translateX(4px);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .patient-incubator-detail {
    padding: 1rem;
  }

  .header-title {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-title h1 {
    font-size: 1.5rem;
  }

  .main-card {
    padding: 1.5rem;
  }

  .card-section {
    flex-direction: column;
    gap: 1rem;
  }

  .time-status {
    flex-direction: column;
    text-align: center;
  }

  .info-grid {
    grid-template-columns: 2fr;
  }

  .invoices-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
    align-items: stretch;
  }

  .add-invoice-btn {
    width: 100%;
    justify-content: center;
  }

  .checkout-btn {
    width: 100%;
    justify-content: center;
  }

  .modal-actions {
    flex-direction: column;
  }
}

.ltr {
  direction: ltr;
}

.rtl {
  direction: rtl;
}

/* Admission Notes Section */
.admission-notes-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 2rem;
}

.add-note-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
  transition: all 0.2s;
}

.add-note-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.notes-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.note-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.5rem;
  transition: all 0.3s;
}

.note-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
}

.note-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;
}

.note-datetime {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #667eea;
  font-size: 1rem;
}

.note-actions {
  display: flex;
  gap: 0.5rem;
}

.note-content {
  margin-top: 1rem;
}

.note-fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.note-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.note-field.full-width {
  grid-column: 1 / -1;
}

.field-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.field-value {
  font-size: 0.9375rem;
  color: #0f172a;
  font-weight: 500;
  word-wrap: break-word;
}

.no-notes {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  color: #94a3b8;
}

.no-notes svg {
  opacity: 0.3;
  margin-bottom: 1rem;
}

.no-notes p {
  margin: 0;
  font-size: 1rem;
}

/* Modal Styles for Admission Notes */
.form-group-modern.required label::after {
  content: " *";
  color: #ef4444;
}

/* Patient Radiology Section */
.patient-radiology-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 2rem;
}

.radiology-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.radiology-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.5rem;
  transition: all 0.3s;
}

.radiology-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
}

.radiology-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;
}

.radiology-info {
  flex: 1;
}

.radiology-name {
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.5rem;
  font-size: 1.125rem;
}

.attachment-count {
  font-size: 0.85rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  display: inline-block;
}

.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.attachment-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.attachment-preview {
  position: relative;
  width: 100%;
  height: 150px;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
}

.attachment-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.attachment-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attachment-hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(102, 126, 234, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
}

.attachment-image-wrapper:hover .attachment-hover-overlay {
  opacity: 1;
}

.attachment-pdf-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #dc3545;
  gap: 0.5rem;
}

.pdf-icon {
  opacity: 0.8;
}

.pdf-label {
  font-size: 0.875rem;
  font-weight: 600;
}

.attachment-action {
  display: flex;
}

.no-attachments-state {
  text-align: center;
  padding: 2rem;
  color: #94a3b8;
}

.no-attachments-icon {
  opacity: 0.4;
  margin-bottom: 0.75rem;
}

.no-attachments-text {
  margin: 0;
  font-size: 0.875rem;
}

.empty-radiology-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #94a3b8;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px dashed #cbd5e1;
}

.empty-radiology-state svg {
  opacity: 0.3;
  margin-bottom: 1rem;
}

.empty-radiology-state h4 {
  color: #64748b;
  margin: 0.5rem 0;
  font-size: 1.125rem;
}

.empty-radiology-state p {
  margin: 0;
  font-size: 0.9375rem;
}

@media (max-width: 768px) {
  .admission-notes-section {
    padding: 1.5rem;
  }

  .note-fields-grid {
    grid-template-columns: 1fr;
  }

  .note-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .patient-radiology-section {
    padding: 1.5rem;
  }

  .radiology-grid {
    grid-template-columns: 1fr;
  }

  .attachments-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}

@media (max-width: 768px) {
  .admission-notes-section {
    padding: 1.5rem;
  }

  .note-fields-grid {
    grid-template-columns: 1fr;
  }

  .note-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

/* Print styles */
.print-only {
  display: none;
}

.hidden {
  display: none !important;
}

@media print {
  .patient-incubator-detail > *:not(.print-only) {
    display: none !important;
  }

  .print-only {
    display: block !important;
  }

  body {
    background: white;
  }
}

/* Action Buttons Section */
.action-buttons-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-buttons button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Print Documents Dropdown */
.print-documents-dropdown-btn .btn {
  background-color: white !important;
  border-color: #e2e8f0 !important;
  color: #0f172a !important;
}

.print-documents-dropdown-btn .btn:hover,
.print-documents-dropdown-btn .btn:focus {
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
  color: #0f172a !important;
}

.print-documents-dropdown {
  min-width: 300px !important;
  background-color: white !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.print-documents-dropdown .dropdown-item {
  padding: 0.75rem 1rem;
  color: #0f172a;
}

.print-documents-dropdown .dropdown-item:hover {
  background-color: #f8fafc;
  color: #0f172a;
}

/* Selected Tests Section */
.selected-tests-section {
  margin-bottom: 1.5rem;
}

.selected-tests-container {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  background-color: #f8f9fa;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
}

.selected-tests-container .table td {
  vertical-align: middle;
  padding: 0.5rem;
}

.selected-tests-container .table thead th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .action-buttons {
    flex-direction: column;
  }

  .action-buttons button {
    width: 100%;
    justify-content: center;
  }
}
</style>
